// src/pages/admin/UserManagement.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Edit, Trash2, Lock, Unlock,
  Shield, Users, KeyRound, Mail, Building2,
  ChevronLeft, ChevronRight, CheckCircle, XCircle,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Input, Select, Loading, LoadingLabel } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import { User, Role } from '../../types';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';

const flattenUnits = (units: any[], level: number = 0): { value: string; label: string }[] => {
  const result: { value: string; label: string }[] = [];
  const indent = '\u00A0\u00A0'.repeat(level);
  const prefix = level > 0 ? '└─ ' : '';
  for (const unit of units) {
    result.push({ value: unit.id.toString(), label: `${indent}${prefix}${unit.name}` });
    if (unit.children_recursive && unit.children_recursive.length > 0) {
      result.push(...flattenUnits(unit.children_recursive, level + 1));
    }
  }
  return result;
};

const roleColorMap: Record<string, { bg: string; text: string; border: string; avatar: string }> = {
  admin: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', avatar: 'bg-rose-500' },
  rektor: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', avatar: 'bg-amber-500' },
  wakil_rektor_1: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', avatar: 'bg-indigo-500' },
  wakil_rektor_2: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', avatar: 'bg-indigo-500' },
  wakil_rektor_3: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', avatar: 'bg-indigo-500' },
  wakil_rektor_4: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', avatar: 'bg-indigo-500' },
  dekan: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', avatar: 'bg-blue-500' },
  kaprodi: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', avatar: 'bg-purple-500' },
  kalem: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', avatar: 'bg-teal-600' },
  kabag: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', avatar: 'bg-orange-500' },
  ka_tu: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', avatar: 'bg-cyan-600' },
  pic: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', avatar: 'bg-emerald-500' },
  lpm: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300', avatar: 'bg-teal-700' },
  senat: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', avatar: 'bg-slate-500' },
};

const getRoleColor = (roleName?: string) =>
  roleColorMap[roleName || ''] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', avatar: 'bg-gray-500' };

const SPECIAL_CHAR_PATTERN = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/;

const PasswordPolicyChecklist: React.FC<{
  password: string;
  settings: any;
}> = ({ password, settings }) => {
  const minLength = Number(settings.password_min_length) || 8;
  const requireUppercase = settings.password_require_uppercase === true;
  const requireNumber = settings.password_require_number === true;
  const requireSpecial = settings.password_require_special === true;

  const rules = [
    { label: `Minimal ${minLength} karakter`, met: password.length >= minLength, required: true },
    { label: 'Huruf besar (A-Z)', met: /[A-Z]/.test(password), required: requireUppercase },
    { label: 'Huruf kecil (a-z)', met: /[a-z]/.test(password), required: true },
    { label: 'Angka (0-9)', met: /[0-9]/.test(password), required: requireNumber },
    { label: 'Karakter spesial (!@#$%^&*)', met: SPECIAL_CHAR_PATTERN.test(password), required: requireSpecial },
  ];

  const score = (rules.filter(r => r.met).length / rules.length) * 100;
  
  const getStrength = () => {
    if (!password) return { label: '', color: '', percent: 0 };
    const requiredMet = rules.filter(r => r.required && r.met).length;
    const requiredTotal = rules.filter(r => r.required).length;
    const allRequiredMet = requiredMet === requiredTotal;
    
    if (!allRequiredMet) return { label: 'Lemah', color: 'bg-danger', percent: score };
    if (score <= 60) return { label: 'Cukup', color: 'bg-warning', percent: score };
    if (score <= 80) return { label: 'Kuat', color: 'bg-info', percent: score };
    return { label: 'Sangat Kuat', color: 'bg-success', percent: score };
  };

  const strength = getStrength();

  return (
    <div className="p-3 bg-neutral-bg rounded-xl border border-neutral-border space-y-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
        Kebijakan Password
      </p>
      <div className="space-y-1.5">
        {rules.map((rule, idx) => (
          <div key={idx} className="flex items-center gap-2">
            {!password ? (
              <div className={`w-3.5 h-3.5 rounded-full border-2 ${rule.required ? 'border-neutral-border' : 'border-neutral-border/50'}`} />
            ) : rule.met ? (
              <CheckCircle className="w-3.5 h-3.5 text-success shrink-0" />
            ) : (
              <XCircle className={`w-3.5 h-3.5 shrink-0 ${rule.required ? 'text-danger' : 'text-neutral-text-tertiary'}`} />
            )}
            <span className={`text-xs ${
              !password ? (rule.required ? 'text-neutral-text-secondary' : 'text-neutral-text-tertiary')
              : rule.met ? 'text-success'
              : rule.required ? 'text-danger font-medium'
              : 'text-neutral-text-secondary'
            }`}>
              {rule.label}
              {!rule.required && <span className="text-[10px] text-neutral-text-tertiary ml-1 opacity-70">(opsional)</span>}
            </span>
          </div>
        ))}
      </div>

      {password && (
        <div className="pt-2 border-t border-neutral-border/40">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-neutral-text-secondary uppercase font-bold">Kekuatan</span>
            <span className={`text-[10px] font-black ${strength.color === 'bg-danger' ? 'text-danger' : strength.color === 'bg-warning' ? 'text-warning' : strength.color === 'bg-info' ? 'text-info' : 'text-success'}`}>
              {strength.label}
            </span>
          </div>
          <div className="w-full h-1.5 bg-neutral-border rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300 ${strength.color}`} style={{ width: `${strength.percent}%` }} />
          </div>
        </div>
      )}
    </div>
  );
};

const UserManagement: React.FC = () => {
  const PER_PAGE = 12;
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [flatUnitOptions, setFlatUnitOptions] = useState<{ value: string; label: string }[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalActiveUsers, setTotalActiveUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [editUser, setEditUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role_id: '', unit_id: '' });
  const [saving, setSaving] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);
  
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  
  const settings = useSystemSettingsStore(state => state.settings);

  useEffect(() => {
    // Pastikan settings terbaru di-fetch
    const loadSettings = async () => {
      const { fetchPublicSettings } = useSystemSettingsStore.getState();
      await fetchPublicSettings();
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [rolesRes, unitsRes] = await Promise.all([adminService.roles.getAll(), adminService.units.getAll()]);
        const rolesData = (rolesRes as any)?.data || rolesRes;
        const unitsData = (unitsRes as any)?.data || unitsRes;
        setRoles(Array.isArray(rolesData) ? rolesData : []);
        setFlatUnitOptions(flattenUnits(Array.isArray(unitsData) ? unitsData : []));
      } catch (e) {
        toast.error('Gagal memuat data master');
      }
    };
    fetchMeta();
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [res, activeRes] = await Promise.all([
        adminService.users.getAll({ page: currentPage, per_page: PER_PAGE, search, role_id: roleFilter ? Number(roleFilter) : undefined }),
        adminService.users.getAll({ page: 1, per_page: 1, is_active: true })
      ]);
      
      const payload = (res as any)?.data || {};
      const rows = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
      setUsers(rows);
      setTotalPages(Number(payload?.last_page || 1));
      setTotalUsers(Number(payload?.total || rows.length));
      
      const activePayload = (activeRes as any)?.data || {};
      setTotalActiveUsers(Number(activePayload?.total || 0));
    } catch (e) {
      toast.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, roleFilter]);

  useEffect(() => { setCurrentPage(1); }, [search, roleFilter]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditUser(null);
    setFormData({ name: '', email: '', password: '', role_id: '', unit_id: '' });
    setShowModal(true);
  };

  const openEdit = (user: User) => {
    setEditUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role_id: user.role?.id.toString() || '', unit_id: user.unit?.id.toString() || '' });
    setShowModal(true);
  };

  const openReset = (user: User) => {
    setResetTarget(user);
    setNewPassword('');
    setShowResetModal(true);
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    const currentSettings = useSystemSettingsStore.getState().settings;
    const minLength = Number(currentSettings.password_min_length) || 8;
    const requireUppercase = currentSettings.password_require_uppercase === true;
    const requireNumber = currentSettings.password_require_number === true;
    const requireSpecial = currentSettings.password_require_special === true;

    if (password.length < minLength) errors.push(`Minimal ${minLength} karakter`);
    if (requireUppercase && !/[A-Z]/.test(password)) errors.push('Harus mengandung huruf besar (A-Z)');
    if (requireNumber && !/[0-9]/.test(password)) errors.push('Harus mengandung angka (0-9)');
    if (requireSpecial && !SPECIAL_CHAR_PATTERN.test(password)) errors.push('Harus mengandung karakter spesial (!@#$%^&*)');
    return errors;
  };

  const handlePasswordChange = (value: string) => {
    setFormData({ ...formData, password: value });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email || !formData.role_id) {
      toast.error('Nama, email, dan role wajib diisi');
      return;
    }
    if (!editUser && !formData.password) {
      toast.error('Password wajib diisi untuk user baru');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Format email tidak valid');
      return;
    }
    if (formData.password) {
      const pwdErrors = validatePassword(formData.password);
      if (pwdErrors.length > 0) {
        toast.error('Password tidak memenuhi kebijakan:\n• ' + pwdErrors.join('\n• '), { duration: 5000 });
        return;
      }
    }

    setSaving(true);
    try {
      const payload: any = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role_id: Number(formData.role_id),
        unit_id: formData.unit_id ? Number(formData.unit_id) : undefined,
      };
      if (formData.password) {
        payload.password = formData.password;
        payload.password_confirmation = formData.password;
      }

      if (editUser) {
        await adminService.users.update(editUser.id, payload);
        toast.success('User berhasil diperbarui');
      } else {
        await adminService.users.create(payload);
        toast.success('User berhasil ditambahkan');
      }
      setShowModal(false);
      fetchUsers();
    } catch (e: any) {
      const status = e?.response?.status;
      const serverMessage = e?.response?.data?.message;
      const errors = e?.response?.data?.errors;
      if (status === 422 && errors) {
        const errorMessages = Object.values(errors).flat();
        toast.error((errorMessages as string[]).join('\n') || 'Validasi gagal', { duration: 5000 });
      } else {
        toast.error(serverMessage || 'Gagal menyimpan user');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Yakin ingin menghapus user ini?')) {
      try { await adminService.users.delete(id); toast.success('User berhasil dihapus'); fetchUsers(); }
      catch (e) { toast.error('Gagal menghapus user'); }
    }
  };

  const handleToggleActive = async (user: User) => {
    try { await adminService.users.toggleActive(user.id); toast.success(`User ${user.is_active ? 'dinonaktifkan' : 'diaktifkan'}`); fetchUsers(); }
    catch (e) { toast.error('Gagal mengubah status user'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      toast.error('Password wajib diisi');
      return;
    }
    const pwdErrors = validatePassword(newPassword);
    if (pwdErrors.length > 0) {
      toast.error('Password tidak memenuhi kebijakan:\n• ' + pwdErrors.join('\n• '), { duration: 5000 });
      return;
    }

    setResetSaving(true);
    try {
      await adminService.users.resetPassword(resetTarget!.id, newPassword);
      toast.success('Password berhasil direset');
      setShowResetModal(false);
      setNewPassword('');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal reset password');
    } finally {
      setResetSaving(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ['Nama', 'Email', 'Role_Name', 'Unit_Code', 'Password'];
    const rows = [
      ['Contoh User', 'user@ubpkarawang.ac.id', 'pic', 'PRODI_IF', 'Password123!'],
      ['Dekan Teknik', 'dekan.ft@ubpkarawang.ac.id', 'dekan', 'FT', '']
    ];
    const csvContent = [headers, ...rows].map(e => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_import_user.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Pilih file CSV terlebih dahulu');
      return;
    }
    setImporting(true);
    try {
      const res = await adminService.users.import(importFile);
      toast.success(res.message || 'Import berhasil');
      setShowImportModal(false);
      setImportFile(null);
      fetchUsers();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal mengimport user');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Admin', href: '/admin' }, { label: 'Manajemen User' }]} />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Manajemen User</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <span className="font-bold text-teal-main">{totalUsers}</span> pengguna terdaftar
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)} icon={<Users className="w-4 h-4" />}>
            Import CSV
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>
            Tambah User
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total User', value: totalUsers, icon: <Users className="w-5 h-5" />, color: 'text-teal-700 bg-teal-50 border-teal-200' },
          { label: 'Aktif', value: totalActiveUsers, icon: <Unlock className="w-5 h-5" />, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
          { label: 'Nonaktif', value: Math.max(0, totalUsers - totalActiveUsers), icon: <Lock className="w-5 h-5" />, color: 'text-rose-700 bg-rose-50 border-rose-200' },
          { label: 'Role', value: roles.length, icon: <Shield className="w-5 h-5" />, color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
        ].map(stat => (
          <div key={stat.label} className={`relative overflow-hidden rounded-xl border p-4 flex items-center gap-3 transition-all hover:shadow-md ${stat.color}`}>
            <div className="p-2 rounded-lg bg-white/70 shadow-sm">{stat.icon}</div>
            <div>
              <p className="text-2xl font-black">{stat.value}</p>
              <p className="text-[11px] font-bold uppercase tracking-wide opacity-70">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
          <input
            type="text"
            placeholder="Cari nama atau email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-neutral-border text-sm bg-white focus:outline-none focus:border-teal-main shadow-sm transition-colors"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-3 rounded-xl border-2 border-neutral-border text-sm bg-white focus:outline-none focus:border-teal-main shadow-sm min-w-[180px] transition-colors"
        >
          <option value="">Semua Role</option>
          {roles.map(r => <option key={r.id} value={r.id.toString()}>{r.display_name}</option>)}
        </select>
      </div>

      <Card padding="sm" className="!p-0 overflow-hidden shadow-sm">
        {loading ? (
          <Loading variant="inline" message="Memuat data pengguna..." className="py-8" />
        ) : users.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-16 h-16 rounded-2xl bg-neutral-bg flex items-center justify-center mx-auto mb-3">
              <Users className="w-8 h-8 text-neutral-text-tertiary" />
            </div>
            <p className="text-base font-bold text-neutral-text-primary">Tidak ada pengguna ditemukan</p>
            <p className="text-sm text-neutral-text-secondary mt-1">
              {search || roleFilter ? 'Coba ubah filter pencarian.' : 'Tambahkan pengguna pertama.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-neutral-bg border-b border-neutral-border">
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary">Pengguna</th>
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary hidden md:table-cell">Email</th>
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary">Role</th>
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary hidden lg:table-cell">Unit</th>
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary">Status</th>
                  <th className="text-right px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-border/50">
                {users.map((user) => {
                  const roleColor = getRoleColor(user.role?.name);
                  const initials = user.name.trim().split(/\s+/).filter(n => n.length > 0 && !n.includes('.') && !n.includes(',')).map(n => n[0]).slice(0, 2).join('').toUpperCase() || '??';
                  return (
                    <tr key={user.id} className="group hover:bg-teal-main/[0.02] transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl ${roleColor.avatar} text-white flex items-center justify-center text-xs font-black shadow-sm flex-shrink-0`}>{initials}</div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-neutral-text-primary truncate">{user.name}</p>
                            <p className="text-xs text-neutral-text-secondary mt-0.5 md:hidden truncate">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <div className="flex items-center gap-1.5 uppercase font-medium text-xs text-neutral-text-secondary">
                          <Mail className="w-3.5 h-3.5" /> {user.email}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold border px-2 py-0.5 rounded-lg ${roleColor.bg} ${roleColor.text} ${roleColor.border}`}>
                          {user.role?.display_name || '-'}
                        </span>
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell">
                        <div className="flex items-center gap-1.5 text-sm text-neutral-text-secondary">
                          <Building2 className="w-3.5 h-3.5" /> {user.unit?.name || '-'}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-lg border ${user.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                          {user.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(user)} className="p-2 rounded-lg hover:bg-neutral-bg text-neutral-text-secondary transition-colors"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => openReset(user)} className="p-2 rounded-lg hover:bg-neutral-bg text-neutral-text-secondary transition-colors"><KeyRound className="w-4 h-4" /></button>
                          <button onClick={() => handleToggleActive(user)} className="p-2 rounded-lg hover:bg-neutral-bg text-neutral-text-secondary transition-colors">
                            {user.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDelete(user.id)} className="p-2 rounded-lg hover:bg-rose-50 text-neutral-text-secondary hover:text-danger transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalUsers > 0 && (
              <div className="flex items-center justify-between px-5 py-4 border-t border-neutral-border bg-neutral-bg/50">
                <p className="text-xs text-neutral-text-secondary">
                  Menampilkan <span className="font-bold">{((currentPage - 1) * PER_PAGE) + 1}</span> – <span className="font-bold">{Math.min(currentPage * PER_PAGE, totalUsers)}</span> dari <span className="font-bold">{totalUsers}</span> pengguna
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1.5 rounded-lg border border-neutral-border hover:bg-white disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1.5 rounded-lg border border-neutral-border hover:bg-white disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editUser ? 'Edit Pengguna' : 'Tambah Pengguna Baru'}
        size="md"
        footer={<><Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button><Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? <LoadingLabel label={editUser ? 'Menyimpan...' : 'Menambahkan...'} /> : editUser ? 'Simpan' : 'Tambah'}</Button></>}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Nama Lengkap" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>
          {!editUser && (
            <div className="space-y-2">
              <Input label="Password" type="password" value={formData.password} onChange={(e) => handlePasswordChange(e.target.value)} required />
              <PasswordPolicyChecklist password={formData.password} settings={settings} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select label="Role" options={roles.map(r => ({ value: r.id.toString(), label: r.display_name }))} value={formData.role_id} onChange={(v) => setFormData({ ...formData, role_id: v })} required />
            <Select label="Unit" options={[{ value: '', label: '— Tanpa Unit —' }, ...flatUnitOptions]} value={formData.unit_id} onChange={(v) => setFormData({ ...formData, unit_id: v })} />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset Password"
        size="sm"
        footer={<><Button variant="outline" onClick={() => setShowResetModal(false)}>Batal</Button><Button variant="primary" onClick={handleResetPassword} disabled={resetSaving}>{resetSaving ? <LoadingLabel label="Mereset..." /> : 'Reset'}</Button></>}
      >
        <div className="space-y-4">
          <Input label="Password Baru" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
          {newPassword && <PasswordPolicyChecklist password={newPassword} settings={settings} />}
        </div>
      </Modal>

      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import User dari CSV"
        size="md"
        footer={<><Button variant="outline" onClick={() => setShowImportModal(false)}>Batal</Button><Button variant="primary" onClick={handleImport} disabled={importing}>{importing ? <LoadingLabel label="Mengimpor..." /> : 'Mulai Import'}</Button></>}
      >
        <div className="space-y-4">
          <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl">
            <h4 className="text-sm font-bold text-teal-800 mb-1">Petunjuk Import</h4>
            <ul className="text-xs text-teal-700 space-y-1 list-disc pl-4">
              <li>File berformat .csv atau .txt</li>
              <li>Kolom: Nama, Email, Role_Name, Unit_Code, Password</li>
              <li>Jika email sudah ada, data akan diupdate.</li>
            </ul>
            <button onClick={handleDownloadTemplate} className="mt-2 text-xs font-bold text-teal-600 underline">Download Template CSV</button>
          </div>
          <div className="border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center">
            <input type="file" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagement;
